import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ViewPaths } from '@/types/views';
import { useCallback, useMemo } from 'react';
import Header from '@/components/Header/Header';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { CenteredColumn } from '@/components/CenteredColumn';
import {
  getViewsForType,
  hasViewPermission,
  isValidView,
} from '@/utils/viewHelpers';
import { DataNotFound } from '@/components/DataNotFound';
import { getDefaultView } from '@/utils/viewHelpers';
import { ViewConfig } from '@/types/views';
import { DeleteSystemAdministratorForm } from '@/components/DeleteSystemAdministratorForm';
import { SystemAdministratorProfileForm } from '@/components/SystemAdministrators/SystemAdministratorProfileForm';
import { Loading } from '@/components/Loading';
import { useListTenantUsers } from '@/api/trafikportalen';
import { FormattedMessage } from 'react-intl';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useAuth } from '@/stores/auth';
import { CurrentUserBadge } from '@/components/CurrentUserBadge';

export const Route = createFileRoute(
  '/$tenantId/_layout/system-administrators/$systemadministratorId/$view',
)({
  component: SystemAdministratorDetailView,
  beforeLoad: ({ params: { tenantId, systemadministratorId, view } }) => {
    if (
      !isValidView(view, 'systemadministrator') ||
      !hasViewPermission(view, 'systemadministrator', 'tenant:viewer')
    ) {
      throw redirect({
        to: '/$tenantId/system-administrators/$systemadministratorId/$view',
        params: {
          tenantId,
          systemadministratorId,
          view: getDefaultView('systemadministrator'),
        },
      });
    }
  },
});

function SystemAdministratorDetailView() {
  const { tenantId, systemadministratorId, view } = Route.useParams();
  const selected = view as ViewPaths<'systemadministrator'>;
  const navigate = useNavigate();
  const { data: systemAdministrators, isLoading } = useListTenantUsers();
  const { userProfile } = useAuth();

  const administrator = systemAdministrators?.find(
    (admin) => admin.id === systemadministratorId,
  );

  const isCurrentUser =
    userProfile?.email && administrator?.email === userProfile.email;

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, systemadministratorId, view },
        replace: false,
      });
    },
    [navigate, tenantId, systemadministratorId],
  );

  const views = useMemo(() => {
    return getViewsForType('systemadministrator') as ViewConfig[];
  }, []);

  const metadataKeys = useMemo(() => {
    const administratorName = administrator
      ? `${administrator.firstname} ${administrator.lastname}`
      : 'Systemadministratör';

    switch (selected) {
      case 'profile':
        return {
          title: `${administratorName} - Profil`,
          description: `Profilinformation för systemadministratören ${administratorName}`,
        };
      case 'advanced':
        return {
          title: `${administratorName} - Avancerat`,
          description: `Avancerade inställningar för systemadministratören ${administratorName}`,
        };
      default:
        return {
          title: `${administratorName}`,
          description: `Detaljer för systemadministratören ${administratorName}`,
        };
    }
  }, [selected, administrator]);

  if (isLoading) {
    return <Loading />;
  }

  if (!administrator) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Systemadministratören finns inte" />
        }
        buttonTitle={
          <FormattedMessage defaultMessage="Till systemadministratörer" />
        }
        onClick={() =>
          navigate({
            to: '/$tenantId/system-administrators',
            params: { tenantId },
          })
        }
      />
    );
  }

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header
        title={
          <span className="flex items-center gap-2">
            <span>
              {administrator.firstname} {administrator.lastname}
            </span>
            {isCurrentUser && <CurrentUserBadge />}
          </span>
        }
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
          />
        }
      />
      <CenteredColumn>
        {selected === 'profile' && (
          <SystemAdministratorProfileForm administrator={administrator} />
        )}
        {selected === 'advanced' && (
          <DeleteSystemAdministratorForm administrator={administrator} />
        )}
      </CenteredColumn>
    </div>
  );
}
